import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, X, Check, ArrowUp, ArrowDown } from 'lucide-react'
import { api } from '../../lib/api.js'
import { useOnline } from '../../lib/useOnline.js'
import { toast } from '../../components/feedback/Toast.jsx'

/**
 * Post Production — the Audiobooks job tracker (Charles, 2026-08-06).
 * A real editable table, Notion-style: tap a cell to edit in place.
 * Status and Publisher are select chips with user-creatable options;
 * Editor/QC is text; the two Due columns are date fields (legacy free-text
 * values from the import stay readable and are replaced the first time a
 * real date is picked). Viewers get the same table, read-only.
 */

// Chip palette — soft Notion-style fills. Content colors, not brand jobs;
// they live here because nothing else in the app uses them.
const CHIP = {
  gray:   { bg: '#EDECE9', fg: '#5A5852' },
  purple: { bg: '#EAE4F2', fg: '#5D3F8E' },
  blue:   { bg: '#DDEBF9', fg: '#2B5E9E' },
  red:    { bg: '#FBE4E4', fg: '#A13B3B' },
  yellow: { bg: '#FBF3DB', fg: '#8A6C1F' },
  green:  { bg: '#DDEDEA', fg: '#2F6B5E' },
  pink:   { bg: '#F7E3EE', fg: '#A0447E' },
  orange: { bg: '#FAE8DD', fg: '#A25F2B' },
}

const chipStyle = (color) => {
  const c = CHIP[color] || CHIP.gray
  return { backgroundColor: c.bg, color: c.fg }
}

export default function AudiobooksPage() {
  const qc = useQueryClient()
  const online = useOnline()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const { data, isLoading } = useQuery({ queryKey: ['pp'], queryFn: () => api('/pp/jobs') })
  const [filter, setFilter] = useState('')
  // Tap a column header to sort A–Z, tap again for Z–A (Charles,
  // 2026-08-06). Display-only — the stored manual order is untouched.
  const [sort, setSort] = useState(null) // null | { key, dir: 1 | -1 }
  const canEdit = ['admin', 'editor'].includes(me.data?.user?.role) && online

  const toggleSort = (key) => setSort((s) => (s?.key === key ? { key, dir: -s.dir } : { key, dir: 1 }))

  const jobs = (data?.jobs || []).filter((j) => {
    const f = filter.trim().toLowerCase()
    if (!f) return true
    return [j.title, j.status, j.publisher, j.editorQc].some((v) => v && v.toLowerCase().includes(f))
  })
  if (sort) {
    jobs.sort((a, b) => {
      const av = (a[sort.key] || '').toLowerCase()
      const bv = (b[sort.key] || '').toLowerCase()
      if (!av && !bv) return 0
      if (!av) return 1 // empties sink, whichever direction
      if (!bv) return -1
      return av.localeCompare(bv) * sort.dir
    })
  }
  const options = data?.options || { status: [], publisher: [] }

  function patchLocal(job) {
    qc.setQueryData(['pp'], (d) => ({ ...d, jobs: d.jobs.map((j) => (j.id === job.id ? job : j)) }))
  }

  async function save(id, patch) {
    try {
      const res = await api(`/pp/jobs/${id}`, { method: 'PATCH', body: patch })
      patchLocal(res.job)
    } catch (err) {
      toast(err.message || 'Couldn’t save', 'error')
      qc.invalidateQueries({ queryKey: ['pp'] })
    }
  }

  async function addJob() {
    try {
      const res = await api('/pp/jobs', { body: {} })
      qc.setQueryData(['pp'], (d) => ({ ...d, jobs: [res.job, ...d.jobs] }))
    } catch (err) {
      toast(err.message || 'Couldn’t add', 'error')
    }
  }

  async function removeJob(j) {
    if (!window.confirm(`Delete “${j.title || 'this job'}”?`)) return
    try {
      await api(`/pp/jobs/${j.id}`, { method: 'DELETE' })
      qc.setQueryData(['pp'], (d) => ({ ...d, jobs: d.jobs.filter((x) => x.id !== j.id) }))
    } catch (err) {
      toast(err.message || 'Couldn’t delete', 'error')
    }
  }

  async function createOption(field, value) {
    const res = await api('/pp/options', { body: { field, value } })
    qc.setQueryData(['pp'], (d) => ({
      ...d,
      options: d.options[field].some((o) => o.value === res.option.value)
        ? d.options
        : { ...d.options, [field]: [...d.options[field], res.option] },
    }))
    return res.option
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3 border-b border-hairline pb-3">
        <h1 className="text-2xl font-semibold text-ink">Audiobooks</h1>
        {canEdit && (
          <button
            onClick={addJob}
            className="tap flex shrink-0 items-center gap-1.5 rounded-xl bg-navy px-3.5 py-2 text-sm font-medium text-white"
          >
            <Plus size={15} /> New job
          </button>
        )}
      </div>

      <input
        type="search"
        placeholder="Filter jobs…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="tap mb-4 w-full rounded-xl border border-hairline bg-grouped px-4 py-2.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link"
      />

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <div className="card scroll-x">
          <table className="w-full min-w-[760px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-hairline">
                <Th sortKey="title" sort={sort} onSort={toggleSort} className="min-w-[220px]">Book Title</Th>
                <Th sortKey="status" sort={sort} onSort={toggleSort}>Status</Th>
                <Th sortKey="publisher" sort={sort} onSort={toggleSort}>Publisher</Th>
                <Th sortKey="editorQc" sort={sort} onSort={toggleSort}>Editor/QC</Th>
                <Th sortKey="dueQc" sort={sort} onSort={toggleSort}>Due to QC</Th>
                <Th sortKey="due" sort={sort} onSort={toggleSort}>Due</Th>
                {canEdit && <Th className="w-8" />}
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-b border-hairline last:border-0 align-top">
                  <td className="px-1 py-0.5">
                    <TextCell value={j.title} canEdit={canEdit} bold onSave={(v) => save(j.id, { title: v })} />
                  </td>
                  <td className="px-1 py-0.5">
                    <SelectCell
                      value={j.status}
                      options={options.status}
                      canEdit={canEdit}
                      onPick={(v) => save(j.id, { status: v })}
                      onCreate={(v) => createOption('status', v)}
                    />
                  </td>
                  <td className="px-1 py-0.5">
                    <SelectCell
                      value={j.publisher}
                      options={options.publisher}
                      canEdit={canEdit}
                      onPick={(v) => save(j.id, { publisher: v })}
                      onCreate={(v) => createOption('publisher', v)}
                    />
                  </td>
                  <td className="px-1 py-0.5">
                    <TextCell value={j.editorQc} canEdit={canEdit} onSave={(v) => save(j.id, { editorQc: v })} />
                  </td>
                  <td className="px-1 py-0.5">
                    <DateCell value={j.dueQc} canEdit={canEdit} onSave={(v) => save(j.id, { dueQc: v })} />
                  </td>
                  <td className="px-1 py-0.5">
                    <DateCell value={j.due} canEdit={canEdit} onSave={(v) => save(j.id, { due: v })} />
                  </td>
                  {canEdit && (
                    <td className="px-1 py-2 text-right">
                      <button
                        onClick={() => removeJob(j)}
                        title="Delete job"
                        className="rounded p-1 text-faint hover:text-danger active:text-danger"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                    {filter ? 'No matches.' : 'No jobs yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-2 text-xs text-faint">
        {canEdit ? 'Tap any cell to edit. Changes save instantly for everyone.' : ''}
      </p>
    </div>
  )
}

function Th({ children, className = '', sortKey, sort, onSort }) {
  if (!sortKey) {
    return (
      <th className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-faint ${className}`}>
        {children}
      </th>
    )
  }
  const active = sort?.key === sortKey
  return (
    <th className={`text-left ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title="Sort"
        className={`flex w-full items-center gap-1 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide active:bg-grouped ${
          active ? 'text-ink' : 'text-faint'
        }`}
      >
        {children}
        {active && (sort.dir === 1 ? <ArrowUp size={11} /> : <ArrowDown size={11} />)}
      </button>
    </th>
  )
}

/** Plain text cell — click to edit in place, saves on blur/Enter. */
function TextCell({ value, canEdit, bold, onSave }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value)
  if (!canEdit || !editing) {
    return (
      <button
        type="button"
        disabled={!canEdit}
        onClick={() => {
          setVal(value)
          setEditing(true)
        }}
        className={`block w-full rounded px-2 py-1.5 text-left ${bold ? 'font-medium' : ''} ${
          value ? 'text-ink' : 'text-faint'
        } ${canEdit ? 'hover:bg-grouped' : ''}`}
      >
        {value || '—'}
      </button>
    )
  }
  return (
    <input
      autoFocus
      type="text"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => {
        setEditing(false)
        if (val.trim() !== value) onSave(val.trim())
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.target.blur()
        if (e.key === 'Escape') {
          setVal(value)
          setEditing(false)
        }
      }}
      className="w-full rounded border border-link bg-white px-2 py-1.5 text-sm text-ink focus:outline-none"
    />
  )
}

/** Select chip cell — dropdown of options plus "create one". */
function SelectCell({ value, options, canEdit, onPick, onCreate }) {
  const [open, setOpen] = useState(false)
  const [adding, setAdding] = useState('')
  const wrap = useRef(null)
  const current = options.find((o) => o.value === value)

  async function pick(v) {
    setOpen(false)
    if (v !== value) onPick(v)
  }

  async function add() {
    const v = adding.trim()
    if (!v) return
    setAdding('')
    setOpen(false)
    try {
      const opt = await onCreate(v)
      onPick(opt.value)
    } catch (err) {
      toast(err.message || 'Couldn’t add option', 'error')
    }
  }

  return (
    <div className="relative" ref={wrap}>
      <button
        type="button"
        disabled={!canEdit}
        onClick={() => setOpen((v) => !v)}
        className={`flex min-h-[34px] w-full items-center rounded px-2 py-1 text-left ${canEdit ? 'hover:bg-grouped' : ''}`}
      >
        {value ? (
          <span className="rounded px-2 py-0.5 text-[13px] font-medium" style={chipStyle(current?.color)}>
            {value}
          </span>
        ) : (
          <span className="text-faint">—</span>
        )}
      </button>

      {open && (
        <>
          {/* click-away veil */}
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-30 mt-1 w-56 rounded-xl border border-hairline bg-white p-1.5 shadow-lg">
            <div className="px-2 pb-1 pt-0.5 text-[11px] text-faint">Select an option or create one</div>
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => pick(o.value)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-grouped"
              >
                <span className="rounded px-2 py-0.5 text-[13px] font-medium" style={chipStyle(o.color)}>
                  {o.value}
                </span>
                {o.value === value && <Check size={13} className="ml-auto text-muted" />}
              </button>
            ))}
            {value && (
              <button
                type="button"
                onClick={() => pick('')}
                className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-xs text-muted hover:bg-grouped"
              >
                <X size={12} /> Clear
              </button>
            )}
            <div className="mt-1 flex items-center gap-1 border-t border-hairline px-1 pt-1.5">
              <input
                type="text"
                placeholder="New option…"
                value={adding}
                onChange={(e) => setAdding(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') add()
                }}
                className="min-w-0 flex-1 rounded border-0 bg-grouped px-2 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-link"
              />
              <button
                type="button"
                onClick={add}
                disabled={!adding.trim()}
                className="rounded p-1.5 text-muted disabled:opacity-40"
                aria-label="Add option"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const ISO = /^\d{4}-\d{2}-\d{2}$/

/** Date cell. Legacy imports hold free text ("2/24") — shown as-is until a
 *  real date is picked, which replaces it. */
function DateCell({ value, canEdit, onSave }) {
  const [picking, setPicking] = useState(false)
  const isIso = ISO.test(value)

  if (!canEdit) {
    return <span className="block px-2 py-1.5 text-ink">{isIso ? fmt(value) : value || <span className="text-faint">—</span>}</span>
  }
  if (!picking && value && !isIso) {
    // legacy text — readable, tap to replace with a real date
    return (
      <button type="button" onClick={() => setPicking(true)} className="block w-full rounded px-2 py-1.5 text-left text-ink hover:bg-grouped">
        {value}
      </button>
    )
  }
  return (
    <input
      type="date"
      value={isIso ? value : ''}
      autoFocus={picking}
      onChange={(e) => onSave(e.target.value)}
      className="w-full rounded border-0 bg-transparent px-1.5 py-1 text-sm text-ink hover:bg-grouped focus:bg-white focus:outline-none focus:ring-1 focus:ring-link"
    />
  )
}

function fmt(iso) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}
